import { API_HOST } from '../config';

const BASE_URL = `${API_HOST}/api/v1/live_campaign/invoice`;

function authHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${localStorage.getItem('access')}`,
    ...extra,
  };
}

async function parseError(response) {
  let payload = {};
  try {
    payload = await response.json();
  } catch (e) {
    payload = {};
  }
  // DRF permission refusals carry their text in `detail`, not `message`.
  const error = new Error(
    payload.message || payload.detail || 'Something went wrong. Please try again.',
  );
  error.status = response.status;
  error.fieldErrors = payload;
  return error;
}

async function request(url, options = {}) {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw await parseError(response);
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

/** Completed campaigns the influencer can still raise an invoice for. */
export function getEligibleCampaigns() {
  return request(`${BASE_URL}/eligible_campaigns`, {
    headers: authHeaders(),
  });
}

/** The influencer's own submitted invoices. */
export function getMyInvoices() {
  return request(`${BASE_URL}/mine`, {
    headers: authHeaders(),
  });
}

/**
 * Submit an invoice for a campaign.
 * `values` carries the form fields; `file` is the invoice PDF/image.
 */
export function submitInvoice(campaignId, values, file) {
  const formData = new FormData();
  formData.append('campaign_id', campaignId);
  formData.append('amount', values.amount);
  formData.append('contact_phone', values.contact_phone);
  formData.append('contact_email', values.contact_email);
  if (values.invoice_number) {
    formData.append('invoice_number', values.invoice_number);
  }
  if (values.post_url) {
    formData.append('post_url', values.post_url);
  }
  if (values.payment_instruction) {
    formData.append('payment_instruction', values.payment_instruction);
  }
  formData.append('invoice', file, file.name);

  // Content-Type is deliberately omitted so the browser sets the
  // multipart boundary itself.
  return request(`${BASE_URL}/submit`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
}

/**
 * Edit an invoice the influencer already submitted. `file` is optional —
 * omitting it keeps the document already on record.
 */
export function editInvoice(invoiceId, values, file) {
  const formData = new FormData();
  if (values.amount !== undefined) formData.append('amount', values.amount);
  if (values.contact_phone !== undefined) formData.append('contact_phone', values.contact_phone);
  if (values.contact_email !== undefined) formData.append('contact_email', values.contact_email);
  if (values.invoice_number !== undefined) {
    formData.append('invoice_number', values.invoice_number);
  }
  if (values.post_url !== undefined) formData.append('post_url', values.post_url);
  if (file) formData.append('invoice', file, file.name);

  return request(`${BASE_URL}/${invoiceId}/edit`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: formData,
  });
}

export function deleteInvoice(invoiceId) {
  return request(`${API_HOST}/api/v1/live_campaign/delete_invoice`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ id: invoiceId }),
  });
}

function queryString(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      params.append(key, value);
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Staff dashboard: every invoice, with optional filters. */
export function getInvoiceDashboard(filters = {}) {
  return request(`${BASE_URL}/dashboard${queryString(filters)}`, {
    headers: authHeaders(),
  });
}

/**
 * Download an export of the dashboard's *current* filters. The endpoints
 * need the bearer token, so this fetches the bytes and hands the browser a
 * blob rather than pointing a link at the URL.
 */
async function downloadExport(path, filters, fallbackName) {
  const response = await fetch(`${BASE_URL}/dashboard/${path}${queryString(filters)}`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw await parseError(response);
  }

  // The API names the file; Content-Disposition is only readable when the
  // server exposes it, so fall back to a sensible local name.
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const blob = await response.blob();

  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = match ? match[1] : fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);

  return {
    included: Number(response.headers.get('X-Invoice-Included')) || null,
    skipped: Number(response.headers.get('X-Invoice-Skipped')) || 0,
  };
}

/** Staff: the filtered invoices as a spreadsheet. */
export function downloadInvoiceXlsx(filters = {}) {
  return downloadExport('export.xlsx', filters, 'invoices.xlsx');
}

/** Staff: the filtered invoice documents themselves, zipped. */
export function downloadInvoiceZip(filters = {}) {
  return downloadExport('export.zip', filters, 'invoices.zip');
}

/** Staff: move an invoice to approved / paid / rejected. */
export function updateInvoiceStatus(invoiceId, payload) {
  return request(`${BASE_URL}/${invoiceId}/status`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// IN-1718: payout details, unseen updates, staff workspace, exception links
// ---------------------------------------------------------------------------

const PAYOUT_URL = `${API_HOST}/api/v1/influencer/bank_detail`;

/** The creator's payout details, or null if none are on file yet. */
export async function getPayoutDetails() {
  const response = await fetch(PAYOUT_URL, { headers: authHeaders() });
  if (response.status === 404) return null;
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * Save payout details. `values` are the text fields; `files` may carry
 * `pan_card_image` and `msme_certificate` File objects.
 */
export function savePayoutDetails(values, files = {}) {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null) formData.append(key, value);
  });
  Object.entries(files).forEach(([key, file]) => {
    if (file) formData.append(key, file, file.name);
  });
  return request(PAYOUT_URL, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
}

/** Clear the creator's "updated" badges once they have seen the page. */
export function markInvoicesSeen() {
  return request(`${BASE_URL}/mine/seen`, {
    method: 'POST',
    headers: authHeaders(),
  });
}

const WORKSPACE_URL = `${BASE_URL}/workspace`;

function jsonPost(url, payload) {
  return request(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
}

/** Staff: campaigns to set up for invoicing, searchable. */
export function searchWorkspaceCampaigns(search = '') {
  return request(`${WORKSPACE_URL}/campaigns${queryString({ search })}`, {
    headers: authHeaders(),
  });
}

/** Staff: existing creators (master data) by name, email or handle. */
export function searchWorkspaceCreators(search = '') {
  return request(`${WORKSPACE_URL}/creators${queryString({ search })}`, {
    headers: authHeaders(),
  });
}

/** Staff: the creators on one campaign, with prices and lock state. */
export function getWorkspaceCampaignCreators(campaignId) {
  return request(`${WORKSPACE_URL}/campaigns/${campaignId}/creators`, {
    headers: authHeaders(),
  });
}

/**
 * Staff: add a creator to a campaign — `{influencer_id}` from master data,
 * or `{name, email}` for someone new — with optional prices and lock.
 */
export function addWorkspaceCreator(campaignId, payload) {
  return jsonPost(`${WORKSPACE_URL}/campaigns/${campaignId}/creators`, payload);
}

/**
 * Staff: take a creator or vendor off a campaign (IN-1821 item 3).
 *
 * Three outcomes, decided by the server: `{result: "removed"}` for a line
 * nobody has invoiced against, `{result: "withdrawn", ...row}` once invoices
 * exist, and 400 with a readable `detail` once anything is paid. The
 * invoices are never touched; they are financial records.
 */
export function removeWorkspaceCreator(campaignId, lineId) {
  return request(`${WORKSPACE_URL}/campaigns/${campaignId}/creators/${lineId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

/**
 * Staff: the project's commercial block (spec §4.1).
 *
 * Live since 28 Oct. `POST /brand/campaign/` accepts the same nine fields on
 * create, so a new project no longer needs this as a second call.
 */
export function updateCampaignCommercials(campaignId, payload) {
  return request(`${WORKSPACE_URL}/campaigns/${campaignId}/commercials`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
}

/**
 * A campaign's posts for one platform, for the campaign the workspace has
 * selected.
 *
 * The product's own feed endpoint. The existing `feeds.js` wrappers read the
 * campaign id out of redux, which the invoices feature does not use, so this
 * takes it explicitly.
 *
 * `page` is 1-based; the feed returns ten rows a page with `next` set when
 * there are more.
 */
export function getCampaignFeed(campaignId, platform, page = 1) {
  return request(
    `${API_HOST}/api/v1/campaign/${campaignId}/social_media/feeds/${platform}${queryString({
      page,
    })}`,
    { headers: authHeaders() },
  );
}

/** Staff: set payout / client billing and lock or unlock them. */
export function setShortlistPrice(shortlistId, payload) {
  return jsonPost(`${WORKSPACE_URL}/shortlist/${shortlistId}/price`, payload);
}

/** Staff: exception links, optionally for one campaign. */
export function getExceptionLinks(campaignId) {
  return request(`${BASE_URL}/exception-links${queryString({ campaign_id: campaignId })}`, {
    headers: authHeaders(),
  });
}

export function createExceptionLink(payload) {
  return jsonPost(`${BASE_URL}/exception-links`, payload);
}

export function revokeExceptionLink(linkId) {
  return jsonPost(`${BASE_URL}/exception-links/${linkId}/revoke`);
}

/**
 * Staff: how the exception path is being used — by submitter, campaign and
 * the staff member who issued the link. Takes the dashboard's filters.
 */
export function getExceptionReport(filters = {}) {
  return request(`${BASE_URL}/exception-report${queryString(filters)}`, {
    headers: authHeaders(),
  });
}

// The public exception form. No token is required; one is sent if present
// only so a staff member filling it in on a creator's behalf is recorded.
function optionalAuth() {
  const token = localStorage.getItem('access');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getPublicInvoiceLink(token) {
  return request(`${BASE_URL}/exception/${encodeURIComponent(token)}`, {
    headers: optionalAuth(),
  });
}

export function submitPublicInvoice(token, values, file) {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') formData.append(key, value);
  });
  formData.append('invoice', file, file.name);
  return request(`${BASE_URL}/exception/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: optionalAuth(),
    body: formData,
  });
}

// ---------------------------------------------------------------------------
// Subsystem 2: tax on an invoice, and the payment runs it is paid through
//
// The payment run endpoints went live on prod on 27 Oct (API #2967 -> #2969),
// so a 404 from them is a fault, not an absence. Tax and the spend rollup are
// still unbuilt — see
// docs/superpowers/specs/2026-10-12-payables-backend-requirements.md — and
// their callers treat a 404 as "not built yet", hiding the section rather
// than showing an error, which is how the commercial block behaves too.
// ---------------------------------------------------------------------------

const RUN_URL = `${API_HOST}/api/v1/live_campaign/payment_run`;

/** Finance: the tax figures on one invoice. */
export function updateInvoiceTax(invoiceId, payload) {
  return request(`${BASE_URL}/${invoiceId}/tax`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
}

/**
 * Finance: the payment runs, newest first.
 *
 * Its one caller drives an advisory notice, so a 404 resolves to an empty
 * list rather than throwing: the notice simply never fires. The endpoint is
 * live, so that path should not be taken.
 */
export async function getPaymentRuns(filters = {}) {
  const response = await fetch(`${RUN_URL}${queryString(filters)}`, { headers: authHeaders() });
  if (response.status === 404) return { results: [] };
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/** Finance: open a run over the given approved invoices. */
export function createPaymentRun(payload) {
  return jsonPost(RUN_URL, payload);
}

/**
 * Finance: record that the bank file for this run has been produced.
 *
 * The file is generated in the browser (D8 — we produce a file, we do not
 * move money); this only records that it happened, so the run can ask for its
 * payment reference afterwards.
 */
export function markPaymentRunExported(runId, payload = {}) {
  return jsonPost(`${RUN_URL}/${runId}/exported`, payload);
}

/** Finance: the spend rollup across campaigns, when the API can serve it. */
export async function getSpendRollup(filters = {}) {
  const response = await fetch(
    `${API_HOST}/api/v1/live_campaign/invoice/spend${queryString(filters)}`,
    { headers: authHeaders() },
  );
  if (response.status === 404) return null;
  if (!response.ok) throw await parseError(response);
  return response.json();
}
